import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { step1Schema } from '@shared/lib/validators/registration-schema';
import { Controller, useForm } from 'react-hook-form';
import {
  selectCurrentStep,
  setStep,
} from '../../../entities/registration/model';
import { useEffect } from 'react';
import { formatPhoneNumber } from '@shared/lib/validators/phone-normalize';
import { useFieldArray } from 'react-hook-form';
import { Button } from '../../../shared/ui/button';
import { InputField } from '../../../shared/ui/input-field';
import redPlusCircle from '../../../shared/assets/sprites/red_plus_circle.svg';
import ReactInputMask from 'react-input-mask';
import logo from '../../../shared/assets/images/register-logo.png';
import back from '../../../shared/assets/sprites/arrow_back.png';
import {
  addCompanyPartialData,
  selectLegalEntities,
} from '../../../entities/registration/model';
import { useAppDispatch, useAppSelector } from '../../../app/store/store';
import { getTaxLabelForLegalEntity } from '@shared/lib/tax-label';

export const RegisterLeftSide = () => {
  return (
    <div>
      <section className="flex justify-center items-center flex-col absolute top-[45%] left-[25%] -translate-x-1/2 -translate-y-[20%] text-center w-[346px]">
        <img src={logo} alt="" />
      </section>
    </div>
  );
};

interface IRegisterHeader {
  title: string;
  description?: string;
  /**
   * Номер текущего шага (1..5). Если не передан или 0 — счётчик
   * не показывается. Раньше тип был обязательным, и
   * `RegistrationStepConfirmation` падал в TS, потому что step
   * там не нужен.
   */
  step?: number;
  handler?: () => void;
  isBack?: boolean;
  stepSize?: number;
}

export const RegisterHeader = ({
  title,
  description,
  step,
  isBack,
  stepSize = 1,
}: IRegisterHeader) => {
  const currentStep = useSelector(selectCurrentStep);
  const dispatch = useAppDispatch();
  const handleBack = () => {
    dispatch(setStep(stepSize));
  };

  // По макету «Назад» и «Шаг N/5» — шапка правой панели: прижаты к её
  // верхним углам, отдельно от формы. Позиционируем относительно панели
  // (AccessLayout: правая колонка — relative). Шапка (top-8, ~24px) кончается
  // выше начала контента (отступы p-8 + p-10 = 72px), поэтому на заголовок
  // и описание не наезжает, как было раньше, когда ряд стоял поверх блока
  // с заголовком.
  const showBack = currentStep !== 0 && isBack;
  const showCounter = typeof step === 'number' && step > 0;

  return (
    <div className="w-full max-w-[70%]">
      {(showBack || showCounter) && (
        <div className="absolute top-8 left-8 right-12 flex items-center justify-between min-h-[24px]">
          {showBack ? (
            <button
              type="button"
              onClick={handleBack}
              className="bg-transparent border-none flex items-center font-medium text-gray-500 hover:text-[#e3282a] transition-colors"
            >
              <img src={back} alt="" />
              <span className="ml-[10px]">Назад</span>
            </button>
          ) : (
            // Пустой spacer, чтобы счётчик уехал в правый край
            // даже когда «Назад» не отображается.
            <span aria-hidden />
          )}
          {showCounter && (
            <div className="text-[#e3282a] font-medium">Шаг {step}/5</div>
          )}
        </div>
      )}
      <div className="text-[#141414] text-[28px] font-bold leading-tight">
        {title}
      </div>
      {description && (
        <p className="text-[#b4b4b4] text-[18px] leading-[25px] mt-2">
          {description}
        </p>
      )}
    </div>
  );
};

export const RegistrationStepCompanyData = () => {
  const newLegalEntities = useAppSelector(selectLegalEntities);
  const dispatch = useAppDispatch();

  // Лейбл налогового номера зависит от страны юр.лица. Источник —
  // адрес из партнёрского инвайта 1С (см. addCompanyData reducer:
  // codeAlfa3='KAZ' → addresses[].country='kz'). Один общий хелпер
  // в shared/lib/tax-label, чтобы все экраны показывали одно и то же.
  const taxNumberLabel = getTaxLabelForLegalEntity(newLegalEntities?.[0]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(step1Schema),
    shouldUnregister: false,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'phones',
  });

  useEffect(() => {
    reset({
      name: newLegalEntities?.[0]?.name,
      inn: newLegalEntities?.[0]?.inn,
      email: newLegalEntities?.[0]?.email,
      online_documents: newLegalEntities?.[0]?.online_documents || false,
      phones:
        newLegalEntities?.[0]?.phones?.map((phone) => ({
          phone: formatPhoneNumber(phone),
        })).length > 0
          ? newLegalEntities?.[0]?.phones?.map((phone) => ({
              phone: formatPhoneNumber(phone),
            }))
          : [{ phone: '' }],
    });
  }, [newLegalEntities, reset]);

  const onSubmit = (data: Record<string, unknown>) => {
    const phones = (data.phones as { phone: string }[]).map((phone) => phone.phone);
    if (data.phoneInput) phones.push(data.phoneInput as string);
    const { phoneInput, ...rest } = data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(addCompanyPartialData({ ...rest, phones } as any));
  };

  return (
    <>
      <RegisterHeader
        step={1}
        isBack={true}
        stepSize={0}
        title="Данные компании"
        description="Заполните регистрационные данные. Это займет пару минут."
      />
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <InputField inputName="name" id="name" label="Название компании" placeholder="Введите название компании" register={register} errors={errors} />
        <InputField inputName="inn" id="inn" label={taxNumberLabel} placeholder={`Введите ${taxNumberLabel}`} register={register} errors={errors} />
        <InputField inputName="email" id="email" label="Email" placeholder="Введите email компании" register={register} errors={errors} />
        {/* Телефоны: подпись — как у остальных полей (mb-2), без лишних
            промежутков между подписью и полем; ошибки массива — под полями. */}
        <div className="my-4 w-full">
          <label
            htmlFor="phone-0"
            className="block mb-2 font-montserrat text-[#2d2c2c] text-[16px] font-medium"
          >
            Номер телефона
          </label>
          {fields.map((item, index) => (
            <div key={item.id} className={index > 0 ? 'mt-3' : ''}>
              <Controller
                name={`phones.${index}.phone`}
                control={control}
                defaultValue={item.phone}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <ReactInputMask
                    mask="+7 (999) 999-99-99"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    id={`phone-${index}`}
                    placeholder="+7 (___) ___-__-__"
                  >
                    <InputField
                      inputName={`phones[${index}].phone`}
                      ref={ref}
                      placeholder="+7 (___) ___-__-__"
                      hasMarginAndPadding={false}
                      errors={errors?.phones?.[index]?.phone?.message}
                      deleteIconHandler={
                        index > 0
                          ? (e) => {
                              e.preventDefault();
                              remove(index);
                            }
                          : undefined
                      }
                    />
                  </ReactInputMask>
                )}
              />
            </div>
          ))}
          {(errors?.phones?.root?.message || errors?.phones?.message) && (
            <p className="text-[#e23235] text-sm font-medium leading-5 mt-1">
              {String(errors?.phones?.root?.message || errors?.phones?.message)}
            </p>
          )}
        </div>
        {fields.length >= 3 ? null : (
          <div
            className="flex cursor-pointer justify-center items-center mt-[10px] w-fit"
            onClick={(e) => {
              e.preventDefault();
              append({ phone: '' });
            }}
          >
            <img src={redPlusCircle} alt="Add phone" className="-mt-[2px] -ml-[2px]" />
            <span className="ml-[10px] text-base font-medium">Добавить номер</span>
          </div>
        )}
        <div className="flex my-5 items-center">
          <input
            className="bg-[#9e9e9e] accent-[#e3282a] h-5 w-5 align-sub cursor-pointer"
            type="checkbox"
            id="online_documents"
            {...register('online_documents')}
          />
          <p className="ml-[10px]">Отправить документы онлайн</p>
        </div>
        <div className="flex justify-end mt-4">
          <Button type="submit" className="!w-[189px]">Продолжить</Button>
        </div>
      </form>
    </>
  );
};
